import { z } from 'zod'

import { messageTypes, ruleStates, units } from '../constants/misc'
import {
  nullableString,
  rangeObjectSchema,
  serverItemSchema,
} from '../utils/schema'
import { unlayerDesignSchema } from '../integrations/unlayer/unlayer.schema'
import { loyaltyBonusRuleSchema } from '../loyalty/loyalty-bonus.schema'
import { profileBaseSchema } from '../schema/profile.schema'
import { conditions, fromUserRules, triggerTypes } from './constants'

type Automation = z.infer<typeof automationSchema>
type TriggerRule = z.infer<typeof triggerRuleSchema>
type TriggerStep = z.infer<typeof triggerStepSchema>

const conditionMap: Record<typeof conditions[number], z.ZodType<any>> = {
  assignedTo: z.object({ eq: serverItemSchema }),
  clientGroup: z.object({ in: z.array(serverItemSchema) }),
  loyaltyPointsActive: rangeObjectSchema.partial(),
  loyaltyPointsLifetime: rangeObjectSchema.partial(),
  loyaltyVisitsActive: rangeObjectSchema.partial(),
  noMessages: rangeObjectSchema.pick({ gt: true, delayUnit: true }),
  noSales: rangeObjectSchema.pick({ gt: true, delayUnit: true }),
  notFirstSale: z.object({ eq: z.boolean() }),
  sale: rangeObjectSchema.partial(),
  saleGroup: z.object({ in: z.array(serverItemSchema) }),
  soldBy: z.object({ eq: serverItemSchema }),
}

// Map each condition to a schema object, preserving type inference
const strictSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('sale'), value: conditionMap.sale }),
  z.object({ type: z.literal('noSales'), value: conditionMap.noSales }),
  z.object({
    type: z.literal('loyaltyPointsActive'),
    value: conditionMap.loyaltyPointsActive,
  }),
  z.object({
    type: z.literal('loyaltyPointsLifetime'),
    value: conditionMap.loyaltyPointsLifetime,
  }),
  z.object({
    type: z.literal('loyaltyVisitsActive'),
    value: conditionMap.loyaltyVisitsActive,
  }),
  z.object({ type: z.literal('assignedTo'), value: conditionMap.assignedTo }),
  z.object({ type: z.literal('soldBy'), value: conditionMap.soldBy }),
  z.object({ type: z.literal('noMessages'), value: conditionMap.noMessages }),
  z.object({
    type: z.literal('notFirstSale'),
    value: conditionMap.notFirstSale,
  }),
  z.object({ type: z.literal('saleGroup'), value: conditionMap.saleGroup }),
  z.object({ type: z.literal('clientGroup'), value: conditionMap.clientGroup }),
] as const)
export type ConditionSchemas = z.infer<typeof triggerRuleConditionSchema>

// fallback schema to allow extra types from server
const extraSchema = z
  .object({
    type: z.string(), // Any type string is allowed
    value: z.any(), // `value` can be any type for extra conditions
  })
  .refine((data) => !(data.type in conditionMap), {
    message: 'Extra type detected',
  })
// Combine strictSchema and extraSchema into a union to allow both strict and extra types
const triggerRuleConditionSchema = z.union([strictSchema, extraSchema])

const triggerRuleBaseSchema = z.object({
  description: nullableString,
  state: z.enum(ruleStates),
  title: z.string(),
  type: z.enum(triggerTypes),
})

const triggerRuleSchema = triggerRuleBaseSchema.merge(
  serverItemSchema.merge(
    z.object({
      conditions: z.array(triggerRuleConditionSchema),
      createdAt: z.string(),
      fromUser: z.any(),
      fromName: nullableString,
      fromEmail: nullableString,
      fromUserRule: z.enum(fromUserRules),
      updatedAt: nullableString,
      user: profileBaseSchema,
    })
  )
)

const triggerStepSchema = serverItemSchema.merge(
  z.object({
    attachmentsEmail: z.array(z.string()).optional(),
    attachmentsSms: z.array(z.string()).optional(),
    delayAmount: z.number(),
    delayUnit: z.enum(units),
    emailMessage: nullableString,
    emailSubject: nullableString,
    loyaltyBonusRule: loyaltyBonusRuleSchema.nullable(),
    messageType: z.enum(messageTypes),
    position: z.number(),
    smsMessage: nullableString,
    unlayerDesign: unlayerDesignSchema.nullable(),
    unlayerImage: nullableString,
    unlayerImageFull: nullableString,
    unlayerTemplateId: nullableString,
  })
)

const triggerRulesSchema = z.array(triggerRuleSchema)

const automationSchema = triggerRuleSchema.merge(
  z.object({ steps: z.array(triggerStepSchema) })
)
const automationsSchema = z.array(automationSchema.omit({ steps: true }))

export type { Automation, TriggerRule, TriggerStep }
export {
  automationSchema,
  automationsSchema,
  triggerRuleBaseSchema,
  triggerRuleConditionSchema,
  triggerRuleSchema,
  triggerRulesSchema,
  triggerStepSchema,
}
